import Layout from "@/components/Layout";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useGridPagination } from "@/hooks/useGridPagination";
import { listDepartments, syncDepartmentsFromApi } from "@/lib/hr/departmentsStore";
import { listDesignations, syncDesignationsFromApi } from "@/lib/hr/designationsStore";
import {
  deleteEmployee,
  employeeApiErrorMessage,
  enrollEmployeeOnBiometricDevice,
  listEmployees,
  syncEmployeesFromApi,
  type EmployeeRecord,
} from "@/lib/hr/employeesStore";
import { cn } from "@/lib/utils";
import { can, getStoredAuthUser } from "@/lib/authUser";
import PermissionDenied from "@/components/PermissionDenied";
import { Briefcase, Building2, CalendarIcon, Edit, Eye, Filter, Fingerprint, LayoutGrid, List, Loader2, Plus, Search, Trash2, User } from "lucide-react";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { isAxiosError } from "axios";

const EMPLOYEE_TYPES = ["Permanent", "Contract", "Probation", "Temporary"] as const;
const EMPLOYEE_STATUSES = ["Active", "Inactive"] as const;

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || "—";
}

function employeeIsActive(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

function parseBasicSalaryAmount(raw: string): number {
  const t = String(raw ?? "").replace(/[^\d]/g, "");
  if (!t) return NaN;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/** Match product card price line (e.g. Rs 7,500,000). */
function formatBasicSalaryLine(amount: string): string {
  const n = parseBasicSalaryAmount(amount);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `Rs ${n.toLocaleString("en-PK")}`;
}

const Employees = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "employee_can_view_any");
  const canViewDetail = can(user, "employee_can_view");
  const canCreate = can(user, "employee_can_create");
  const canUpdate = can(user, "employee_can_update");
  const canDelete = can(user, "employee_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [enrollingEmployeeId, setEnrollingEmployeeId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filterDepartmentId, setFilterDepartmentId] = useState<string>("__all__");
  const [filterDesignationId, setFilterDesignationId] = useState<string>("__all__");
  const [filterEmployeeType, setFilterEmployeeType] = useState<string>("__all__");
  const [filterStatus, setFilterStatus] = useState<string>("__all__");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<
    | "status_active_first"
    | "status_inactive_first"
    | "basic_desc"
    | "basic_asc"
    | "latest"
    | "oldest"
    | "name_asc"
    | "name_desc"
    | "code_asc"
  >("status_active_first");
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");

  const departments = useMemo(() => listDepartments(), [version]);
  const designations = useMemo(() => listDesignations(), [version]);
  const employees = useMemo(() => listEmployees(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") {
        setUser(getStoredAuthUser());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setIsForbidden(false);
      try {
        await Promise.all([syncEmployeesFromApi(), syncDepartmentsFromApi(), syncDesignationsFromApi()]);
        if (mounted) refresh();
      } catch (e) {
        if (mounted && isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error(e);
        if (mounted) toast.error(employeeApiErrorMessage(e));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [refresh, navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return employees.filter((e) => {
      if (filterDepartmentId !== "__all__" && e.departmentId !== filterDepartmentId) return false;
      if (filterDesignationId !== "__all__" && e.designationId !== filterDesignationId) return false;
      if (filterEmployeeType !== "__all__" && e.employeeType !== filterEmployeeType) return false;
      if (filterStatus !== "__all__" && e.employeeStatus !== filterStatus) return false;

      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(e.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }

      if (quickDate !== "all") {
        const created = parseISO(e.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }

      if (!q) return true;
      const name = employeeDisplayName(e).toLowerCase();
      return (
        name.includes(q) ||
        (e.employeeCode || "").toLowerCase().includes(q) ||
        (e.departmentName || "").toLowerCase().includes(q) ||
        (e.designationName || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.cnic || "").toLowerCase().includes(q) ||
        (e.primaryContact || "").toLowerCase().includes(q)
      );
    });
  }, [
    employees,
    query,
    filterDepartmentId,
    filterDesignationId,
    filterEmployeeType,
    filterStatus,
    filterCreatedDate,
    quickDate,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "status_active_first": {
          const sa = employeeIsActive(a) ? 0 : 1;
          const sb = employeeIsActive(b) ? 0 : 1;
          if (sa !== sb) return sa - sb;
          return employeeDisplayName(a).localeCompare(employeeDisplayName(b));
        }
        case "status_inactive_first": {
          const sa = employeeIsActive(a) ? 1 : 0;
          const sb = employeeIsActive(b) ? 1 : 0;
          if (sa !== sb) return sa - sb;
          return employeeDisplayName(a).localeCompare(employeeDisplayName(b));
        }
        case "basic_asc": {
          const ga = parseBasicSalaryAmount(a.basicSalary);
          const gb = parseBasicSalaryAmount(b.basicSalary);
          return (Number.isFinite(ga) ? ga : 0) - (Number.isFinite(gb) ? gb : 0);
        }
        case "basic_desc": {
          const ga = parseBasicSalaryAmount(a.basicSalary);
          const gb = parseBasicSalaryAmount(b.basicSalary);
          return (Number.isFinite(gb) ? gb : 0) - (Number.isFinite(ga) ? ga : 0);
        }
        case "latest":
          return b.createdAt.localeCompare(a.createdAt);
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return employeeDisplayName(b).localeCompare(employeeDisplayName(a));
        case "code_asc":
          return (a.employeeCode || "").localeCompare(b.employeeCode || "", undefined, { numeric: true });
        case "name_asc":
        default:
          return employeeDisplayName(a).localeCompare(employeeDisplayName(b));
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterDepartmentId !== "__all__" ? 1 : 0) +
    (filterDesignationId !== "__all__" ? 1 : 0) +
    (filterEmployeeType !== "__all__" ? 1 : 0) +
    (filterStatus !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0);

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterDepartmentId}|${filterDesignationId}|${filterEmployeeType}|${filterStatus}|${filterCreatedDate?.toISOString() ?? ""}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, { defaultPageSize: 20 });

  function statusBadge(e: EmployeeRecord) {
    const active = (e.employeeStatus || "").toLowerCase() === "active";
    return active
      ? { label: "Active", cls: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" }
      : { label: e.employeeStatus || "Inactive", cls: "bg-muted text-muted-foreground" };
  }

  const runEnrollOnDevice = async (employee: EmployeeRecord) => {
    if (!canUpdate || enrollingEmployeeId) return;
    if (employee.deviceUid?.trim()) return;
    if (!employee.employeeCode?.trim()) {
      toast.error("Employee code is required before syncing to a biometric device.");
      return;
    }

    setEnrollingEmployeeId(employee.id);
    try {
      const result = await enrollEmployeeOnBiometricDevice(employee.id);
      refresh();
      const deviceHint = result.ipAddress ? ` (${result.ipAddress})` : "";
      toast.success(`${result.message}${deviceHint}`);
    } catch (e) {
      console.error("[Employees] Enroll on device failed", e);
      toast.error(employeeApiErrorMessage(e));
    } finally {
      setEnrollingEmployeeId(null);
    }
  };

  const isEmployeeSynced = (employee: EmployeeRecord) => Boolean(employee.deviceUid?.trim());

  return (
    isForbidden ? (
      <PermissionDenied
        title="Employees access required"
        message="You don’t have permission to view employees. Ask an administrator to grant employee_can_view_any."
      />
    ) : (
    <Layout title="Employees">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">Employees</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your staff records</p>
          </div>
          {canCreate ? (
            <Link to="/employees/add">
              <Button variant="premium" className="gap-2 shrink-0">
                <Plus className="w-4 h-4" />
                <span className="sm:hidden">Add</span>
                <span className="hidden sm:inline">Add Employee</span>
              </Button>
            </Link>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, department, designation, email, CNIC…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter employees</SheetTitle>
                  <SheetDescription>Refine by department, designation, type, status, or created date.</SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Department</Label>
                      <Select value={filterDepartmentId} onValueChange={setFilterDepartmentId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All departments" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All departments</SelectItem>
                          {departments.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Designation</Label>
                      <Select value={filterDesignationId} onValueChange={setFilterDesignationId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All designations" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All designations</SelectItem>
                          {designations.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee type</Label>
                      <Select value={filterEmployeeType} onValueChange={setFilterEmployeeType}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All types</SelectItem>
                          {EMPLOYEE_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All statuses</SelectItem>
                          {EMPLOYEE_STATUSES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                <SheetFooter className="mt-6 gap-2">
                  <Button
                    type="button"
                    variant="glass"
                    className="w-full"
                    onClick={() => {
                      setFilterDepartmentId("__all__");
                      setFilterDesignationId("__all__");
                      setFilterEmployeeType("__all__");
                      setFilterStatus("__all__");
                      setFilterCreatedDate(undefined);
                    }}
                  >
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="employees-page-size" pageSize={pageSize} onPageSizeChange={setPageSize} />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status_active_first">Status: Active first</SelectItem>
                  <SelectItem value="status_inactive_first">Status: Inactive first</SelectItem>
                  <SelectItem value="basic_desc">Basic salary: High to Low</SelectItem>
                  <SelectItem value="basic_asc">Basic salary: Low to High</SelectItem>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="code_asc">Employee code</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="employees-page-size-desktop" pageSize={pageSize} onPageSizeChange={setPageSize} />

            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status_active_first">Status: Active first</SelectItem>
                    <SelectItem value="status_inactive_first">Status: Inactive first</SelectItem>
                    <SelectItem value="basic_desc">Basic salary: High to Low</SelectItem>
                    <SelectItem value="basic_asc">Basic salary: Low to High</SelectItem>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="code_asc">Employee code</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading employees…
          </div>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {employees.length === 0
              ? canCreate
                ? "No employees yet. Click “Add Employee” to create one."
                : "No employees yet."
              : "No employees match your search."}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((e, i) => {
              const badge = statusBadge(e);
              const basicLine = formatBasicSalaryLine(e.basicSalary);
              const codePrefix = e.employeeCode ? `${e.employeeCode} · ` : "";
              return (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="h-full"
                >
                  <CatalogEntityCard
                    className="h-full flex flex-col"
                    title={employeeDisplayName(e)}
                    titleIcon={User}
                    avatarUrl={e.photoDataUrl}
                    statusBadge={{ label: badge.label, className: badge.cls }}
                    primaryMeta={{
                      label: "Department",
                      value: e.departmentName || "—",
                      icon: Building2,
                      iconClassName: "text-emerald-600",
                    }}
                    secondaryMeta={{
                      label: "Designation",
                      value: e.designationName || "—",
                      icon: Briefcase,
                      iconClassName: "text-red-600",
                    }}
                    footer={`${codePrefix}Last updated: ${formatCatalogAuditDate(e.updatedAt)}`}
                    footerRight={basicLine}
                    footerRightClassName="text-primary font-semibold"
                    onView={canViewDetail ? () => navigate(`/employees/${e.id}`) : undefined}
                    onConnectDevice={canUpdate ? () => void runEnrollOnDevice(e) : undefined}
                    connectDeviceLoading={enrollingEmployeeId === e.id}
                    connectDeviceDisabled={isEmployeeSynced(e)}
                    onEdit={canUpdate ? () => navigate(`/employees/${e.id}/edit`) : undefined}
                    onDelete={canDelete ? () => setDeleteTargetId(e.id) : undefined}
                  />
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((e, i) => {
              const badge = statusBadge(e);
              const basicLine = formatBasicSalaryLine(e.basicSalary);
              const created = parseISO(e.createdAt);
              return (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="glass-card p-3"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                      {e.photoDataUrl ? (
                        <img src={e.photoDataUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-muted-foreground/40" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground truncate">{employeeDisplayName(e)}</h3>
                        <span className={cn("text-[11px] px-2 py-0.5 rounded-full font-medium", badge.cls)}>{badge.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {e.departmentName || "—"} · {e.designationName || "—"} · {e.employeeCode || "—"}
                      </p>
                    </div>
                    <div className="text-right ml-auto">
                      <p className="text-sm font-semibold text-primary">{basicLine}</p>
                      <p className="text-[11px] text-muted-foreground">{isValid(created) ? format(created, "PP") : "—"}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
                      {canUpdate ? (
                        <Button
                          type="button"
                          variant="glass"
                          size="sm"
                          className="gap-1.5 h-8 text-xs"
                          disabled={enrollingEmployeeId === e.id || isEmployeeSynced(e)}
                          onClick={() => void runEnrollOnDevice(e)}
                        >
                          {enrollingEmployeeId === e.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Fingerprint className="w-3 h-3" />
                          )}
                          {isEmployeeSynced(e) ? "Synced" : "Sync"}
                        </Button>
                      ) : null}
                      {canViewDetail ? (
                        <Link to={`/employees/${e.id}`}>
                          <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs">
                            <Eye className="w-3 h-3" /> View
                          </Button>
                        </Link>
                      ) : null}
                      {canUpdate ? (
                        <Link to={`/employees/${e.id}/edit`}>
                          <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs">
                            <Edit className="w-3 h-3" /> Edit
                          </Button>
                        </Link>
                      ) : null}
                      {canDelete ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                          onClick={() => setDeleteTargetId(e.id)}
                          aria-label="Delete employee"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        <GridPaginationFooter
          totalCount={totalCount}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />

        <p className="text-xs text-muted-foreground px-1">
          Reference:{" "}
          <Link to="/departments" className="text-primary font-medium hover:underline">
            Departments
          </Link>{" "}
          ·{" "}
          <Link to="/designations" className="text-primary font-medium hover:underline">
            Designations
          </Link>
        </p>

        <ConfirmAlertDialog
          open={deleteTargetId !== null}
          onOpenChange={(o) => !o && setDeleteTargetId(null)}
          title="Delete this employee?"
          description="This will remove the employee from the list. This action cannot be undone."
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={async () => {
            if (!deleteTargetId) return;
            if (!canDelete) {
              toast.error("User does not have the right permissions.");
              return;
            }
            try {
              const res = await deleteEmployee(deleteTargetId);
              toast.success(res.message);
              setDeleteTargetId(null);
              refresh();
            } catch (e) {
              toast.error(employeeApiErrorMessage(e));
            }
          }}
        />
      </div>
    </Layout>
    )
  );
};

export default Employees;
